/*
 * Rendering "we don't know" without it looking like "zero" (R-016).
 *
 * The Laravel Command Center returns `null` for any metric whose source module
 * isn't ported yet, and a `sources` block naming what's missing
 * (backendv2 app/Services/CommandCenter/ModuleSource.php). The whole point of
 * that contract is lost if the UI renders null the same way it renders 0 — the
 * prototype's F37 was a clinical safety dashboard reporting a fully-stable ward
 * from data it never had.
 *
 * So: a null metric never becomes a digit, a bar width, or a donut segment.
 */

/** Human labels for the backend's source-module keys. */
export const MODULE_LABELS = {
  rpm: "Remote Patient Monitoring",
  onboarding: "Onboarding",
  fleet: "Fleet / Home Visits",
  hotline: "Nurse Hotline",
  medications: "Medications (MAR)",
  orders: "Orders",
  clinical_docs: "Clinical Documentation",
  care_teams: "Care Teams",
  patients: "Patients",
  teleconsult: "Teleconsult",
  encounters: "Encounters",
  users: "Users",
  facilities: "Facilities",
  allergies: "Allergies",
};

export function moduleLabel(key) {
  return MODULE_LABELS[key] || key;
}

/** A value the API could not compute. `0` is a real answer; null/undefined are not. */
export function isUnavailable(value) {
  return value === null || value === undefined;
}

/** The modules a payload reports as unported, as a label list. */
export function unportedModules(sources) {
  if (!sources) return [];
  return Object.entries(sources)
    .filter(([, meta]) => meta?.status === "unported")
    .map(([key]) => moduleLabel(key));
}

/**
 * Percentage of a whole — null unless BOTH operands are real numbers.
 *
 * The prototype's version returned 0 when the numerator was missing, and that 0
 * was rendered as a bar width and a donut slice. A missing numerator has no
 * percentage; it must not be drawable.
 */
export function pct(part, total) {
  if (isUnavailable(part) || isUnavailable(total) || total === 0) return null;
  return Math.round((part / total) * 1000) / 10;
}

/**
 * Chart data guard: null unless every series value is present.
 *
 * Recharts happily plots `{value: null}` as an empty segment, which on a donut
 * reads as "this category is empty" rather than "this category is unknown".
 * Callers render an explanatory panel when this returns null.
 */
export function chartDataOrNull(rows, key = "value") {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return rows.some((r) => isUnavailable(r?.[key])) ? null : rows;
}
